import { Box, Chip, type ChipProps, darken } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import type { NavLinkProps } from "react-router";

export type ChipRowStyle = "badge" | "category";

const ROW_CHIP_RADIUS = 5;

const ROW_CHIP_PADDING: Record<ChipRowStyle, { y: number; x: number }> = {
  badge: { y: 4, x: 7 },
  category: { y: 3, x: 8 },
};

const ROW_CHIP_LABEL: Record<ChipRowStyle, { lineHeight: number | string; letterSpacing: string }> = {
  badge: { lineHeight: 1, letterSpacing: "0.2px" },
  category: { lineHeight: "normal", letterSpacing: "normal" },
};

export const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$rowStyle",
})<{ $rowStyle?: ChipRowStyle }>(({ theme, $rowStyle }) => ({
  display: "flex",
  alignItems: "center",
  borderRadius: $rowStyle ? ROW_CHIP_RADIUS : theme.shape.borderRadius,
  backgroundColor:
    theme.palette.mode === "dark" ? alpha("#afafaf", 0.5) : "inherit",
}));

const CHIP_HORIZONTAL_PADDING = 3;
const CHIP_LABEL_HORIZONTAL_PADDING = 8;
const LIST_CHIP_SCALE = 0.85;

export const INSIGHT_CHIP_PADDING = {
  horizontal: CHIP_HORIZONTAL_PADDING,
  labelHorizontal: CHIP_LABEL_HORIZONTAL_PADDING,
} as const;

export const INSIGHT_META_TYPOGRAPHY = {
  fontSize: "12px",
  lineHeight: "18px",
  fontWeight: 500,
} as const;

export const INSIGHT_CHIP_HEIGHT = 20;

export const LIST_CHIP_HEIGHT = Math.round(INSIGHT_CHIP_HEIGHT * LIST_CHIP_SCALE);
export const LIST_META_TYPOGRAPHY = {
  fontSize: `${Math.round(parseInt(INSIGHT_META_TYPOGRAPHY.fontSize, 10) * LIST_CHIP_SCALE)}px`,
  lineHeight: `${Math.round(parseInt(INSIGHT_META_TYPOGRAPHY.lineHeight, 10) * LIST_CHIP_SCALE)}px`,
  fontWeight: 500,
} as const;

export const LIST_CHIP_PADDING = {
  horizontal: Math.round(CHIP_HORIZONTAL_PADDING * LIST_CHIP_SCALE),
  labelHorizontal: Math.round(CHIP_LABEL_HORIZONTAL_PADDING * LIST_CHIP_SCALE),
} as const;

export const CATEGORY_CHIP_TEXT_INSET_X =
  CHIP_HORIZONTAL_PADDING + CHIP_LABEL_HORIZONTAL_PADDING;

export const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) =>
    prop !== "$color" &&
    prop !== "$bgColor" &&
    prop !== "$insight" &&
    prop !== "$rowStyle",
})<
  | (ChipProps & {
      $color?: string;
      $bgColor?: string;
      $insight?: boolean;
      $rowStyle?: ChipRowStyle;
    })
  | (ChipProps & {
      $color?: string;
      $bgColor?: string;
      $insight?: boolean;
      $rowStyle?: ChipRowStyle;
    } & NavLinkProps)
>(({ $color, $bgColor, $insight = false, $rowStyle, theme }) => {
  const chipHeight = $insight ? INSIGHT_CHIP_HEIGHT : LIST_CHIP_HEIGHT;
  const typography = $insight ? INSIGHT_META_TYPOGRAPHY : LIST_META_TYPOGRAPHY;
  const padding = $insight ? INSIGHT_CHIP_PADDING : LIST_CHIP_PADDING;

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: chipHeight,
    minHeight: chipHeight,
    maxHeight: chipHeight,
    boxSizing: "border-box",
    fontSize: typography.fontSize,
    padding: 0,
    paddingLeft: padding.horizontal,
    paddingRight: padding.horizontal,
    cursor: "pointer",
    ...($color &&
      $bgColor && {
        background: theme.palette.mode === "dark" ? $color : $bgColor,
        color: theme.palette.mode === "dark" ? $bgColor : $color,
        transition: `color 0.1s`,
        "&:hover": {
          color:
            theme.palette.mode === "dark"
              ? darken($bgColor, 0.15)
              : darken($color, 0.35),
        },
      }),
    "& .MuiChip-label": {
      ...typography,
      display: "flex",
      alignItems: "center",
      padding: 0,
      paddingLeft: padding.labelHorizontal,
      paddingRight: padding.labelHorizontal,
      height: "100%",
    },

    "& .MuiChip-icon": {
      opacity: 0.8,
      marginLeft: theme.spacing(0.25),
      marginRight: -2,
      fontSize: $insight ? 14 : 12,
      width: $insight ? 14 : 12,
      height: $insight ? 14 : 12,
      color: theme.palette.mode === "dark" ? $bgColor : $color,
    },

    ...($rowStyle && {
      height: "auto",
      minHeight: 0,
      maxHeight: "none",
      gap: 3,
      paddingTop: ROW_CHIP_PADDING[$rowStyle].y,
      paddingBottom: ROW_CHIP_PADDING[$rowStyle].y,
      paddingLeft: ROW_CHIP_PADDING[$rowStyle].x,
      paddingRight: ROW_CHIP_PADDING[$rowStyle].x,
      border: "none",
      borderRadius: ROW_CHIP_RADIUS,
      fontSize: 12,

      "& .MuiChip-label": {
        display: "block",
        height: "auto",
        padding: 0,
        fontSize: 12,
        fontWeight: 600,
        ...ROW_CHIP_LABEL[$rowStyle],
      },

      "& .MuiChip-icon": {
        margin: 0,
        opacity: 1,
        fontSize: 13,
        width: 13,
        height: 13,
        color: theme.palette.mode === "dark" ? $bgColor : $color,
      },
    }),
  };
});
